<template>
  <div class="upload">
    <label class="label">
      <span
        v-if="status === 'ok' || status === 'error'"
        class="icon is-normal"
        :class="iconClass"
      >
        <font-awesome-icon :icon="icon" />
      </span>
      <Loader
        v-else-if="status === 'validating'"
        small
        :is-loading="true"
      />
      {{ label }}
      <FieldHelp :text="help" />
    </label>
    <div class="file has-name is-fullwidth">
      <label class="file-label">
        <input
          :ref="refName"
          class="file-input"
          :name="refName"
          type="file"
          @input="handleFileUpload()"
        >
        <span class="file-cta">
          <span class="file-icon">
            <font-awesome-icon icon="upload" />
          </span>
          <span class="file-label">Choose a file…</span>
        </span>
        <span class="file-name my-file-name">{{ filename }}</span>
      </label>
    </div>
    <p class="help is-large with-space">
      File must be in CSV format
      <span>(</span>
      <a
        :href="`${publicPath}${exampleFile}`"
        target="_blank"
        rel="noopener noreferrer"
      >download example</a>). Hover over help icon for more information.
    </p>
  </div>
</template>

<script>
import Papa from 'papaparse';
import Loader from '../Loader.vue';
import FieldHelp from './FieldHelp.vue';

Papa.parsePromise = (file) => new Promise((complete, error) => {
  Papa.parse(
    file,
    {
      complete,
      delimiter: ',',
      dynamicTyping: true,
      error,
      header: true,
      transformHeader: (header) => header.toLowerCase(),
      skipEmptyLines: 'greedy',
    },
  );
});

function validateFile(file) {
  const forbiddenFilename = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])$|([<>:"/\\|?*])|(\.|\s)$/ig;
  const allowedFileExtensions = ['csv', 'txt'];

  if (file) {
    if (file.name !== '') {
      if (forbiddenFilename.test(file.name)) {
        throw new Error(`Invalid filename \`${file.name}\` for upload.`);
      }
      const extension = file.name.toLowerCase().split('.').pop();
      if (!allowedFileExtensions.includes(extension)) {
        throw new Error(`File of type \`${extension}\` not accepted. Must be one of ${allowedFileExtensions.join(', ')}.`);
      }
    } else {
      throw new Error('Filename empty.');
    }
  } else {
    throw new Error('File not found.');
  }
}

function validateHeaders(headers, expectedHeaders) {
  for (let i = 0; i < expectedHeaders.length; i += 1) {
    const expected = expectedHeaders[i];
    if (!headers.includes(expected)) {
      throw new Error(`Error parsing uploaded file. Could not find header \`${expectedHeaders[i]}\` in first line of CSV: ${headers}`);
    }
  }
}

function validateRows(rows, errors) {
  if (rows.length === 0) {
    throw new Error('Expecting at least one row of data.');
  }
  const errorMessages = [];
  for (let i = 0; i < errors.length; i += 1) {
    const e = errors[i];
    const offset = 2; // from line in file to row in data (first line is headers and then +1 for 0-based index)
    if (e.code === 'TooManyFields') {
      errorMessages.push(`Found extra data in line ${e.row + offset}.`);
    } else {
      errorMessages.push(`${e.message} in line ${e.row + offset}.`);
    }
  }
  if (errorMessages.length > 0) throw new Error(errorMessages.join('<br />'));
}

function validatePositiveColumns(rows, columnsToValidate) {
  rows.forEach((row, index) => {
    columnsToValidate.forEach((col) => {
      if (row[col] <= 0) throw new Error(`Column "${col}" in row ${index + 2} must be positive, found "${row[col]}".`);
    });
  });
}

export default {
  components: {
    FieldHelp,
    Loader,
  },
  props: {
    exampleFile: String,
    headers: Array,
    help: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: 'Upload',
    },
    refName: {
      type: String,
      default: 'unique-reference',
    },
    modelValue: Object,
    positiveColumns: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      publicPath: process.env.BASE_URL,
    };
  },
  computed: {
    icon() {
      return this.modelValue.status === 'ok' ? 'check' : 'times';
    },
    iconClass() {
      return this.modelValue.status === 'ok' ? 'has-text-success' : 'has-text-danger';
    },
    status() {
      if (this.modelValue == null) return '';
      return this.modelValue.status;
    },
    filename() {
      if (this.modelValue == null) return '';
      return this.modelValue.filename;
    },
  },
  methods: {
    async handleFileUpload() {
      const [file] = this.$refs[this.refName].files;
      this.$emit('update:modelValue', {
        filename: file.name,
        status: 'validating',
        data: null,
      });

      // workaround for Chrome to re-fire the change event
      // when the same file is uploaded twice in a row
      // (but maybe with different content)
      this.$refs[this.refName].value = '';

      try {
        validateFile(file);
        const parsed = await Papa.parsePromise(file);
        const { fields } = parsed.meta;
        validateHeaders(fields, this.headers);
        const { data, errors } = parsed;
        validateRows(data, errors);
        if (this.positiveColumns.length > 0) {
          validatePositiveColumns(data, this.positiveColumns);
        }
        this.$emit('update:modelValue', {
          filename: file.name,
          status: 'ok',
          data,
        });
      } catch (error) {
        this.$emit('update:modelValue', {
          filename: file.name,
          status: 'error',
          data: null,
        });
        this.$store.dispatch('modal/error', error);
      }
    },
  },
};
</script>

<style scoped>
div.upload {
  margin-top: 0.5em;
}
</style>
